import { useInfiniteQuery, useQuery } from "@tanstack/react-query";

import {
  fetchBrowserList,
  fetchFilteredTestCases,
  fetchTestCaseStatus,
  fetchDefectStatus,
  fetchActiveTestCaseRun,
  fetchRecentlyFailedTestCases,
  fetchProjectsList,
  fetchVersionsList,
  fetchTestPlanSchedule,
  fetchWorkspacesList,
  fetchOrgProjectsApi,
} from "./dashboard.api";

import { useFetchUserPreference } from "@/common/services/users/users.queries";
import type { TestPlanScheduleParams } from "@/common/types/TestCase";

interface QueryParams {
  versionId?: number;
  page?: number;
  size?: number;
  query?: string;
  sort?: string;
}

export const useBrowserList = (id: string) =>
  useQuery({
    queryKey: ["browserList", id],
    queryFn: () => fetchBrowserList(id),
    enabled: !!id,
  });

export const useFilteredTestCases = (id: string) =>
  useQuery({
    queryKey: ["filteredTestCases", id],
    queryFn: () => fetchFilteredTestCases(id),
    enabled: !!id,
  });

export const useTestCaseStatus = (id: string) =>
  useQuery({
    queryKey: ["testCaseStatus", id],
    queryFn: () => fetchTestCaseStatus(id),
    enabled: !!id,
  });

export const useDefectStatus = (id: string) =>
  useQuery({
    queryKey: ["defectStatus", id],
    queryFn: () => fetchDefectStatus(id),
    enabled: !!id,
  });

export const useActiveTestCaseRun = (id: string) =>
  useQuery({
    queryKey: ["activeTestCaseRun", id],
    queryFn: () => fetchActiveTestCaseRun(id),
    enabled: !!id,
  });

export const useRecentlyFailedTestCases = (filterId: string) =>
  useQuery({
    queryKey: ["recentlyFailedTestCases", filterId],
    queryFn: () => fetchRecentlyFailedTestCases(filterId),
    enabled: !!filterId,
  });

export const useTestPlanSchedule = (params?: TestPlanScheduleParams) =>
  useQuery({
    queryKey: ["testPlanSchedule", params],
    queryFn: () => {
      if (!params)
        throw new Error("Params are required for test plan schedule");
      return fetchTestPlanSchedule(params);
    },
    enabled: !!params,
  });

export const useProjectList = (enabled = true, options: { excludeSample?: boolean } = {}) => {
  const { data: prefData } = useFetchUserPreference();
  const orgId: number | null = prefData?.data?.activeOrganizationId ?? null;
  const excludeSample = options.excludeSample === true;
  return useQuery({
    queryKey: ["projectList", orgId, excludeSample],
    queryFn: () => fetchProjectsList({ size: 500, sort: "id,asc", excludeSample: excludeSample || undefined }),
    enabled,
  });
};

export const useProjectListInfinite = (
  params?: Omit<QueryParams, "page">,
  options?: { enabled?: boolean },
) => {
  const { data: prefData } = useFetchUserPreference();
  const orgId: number | null = prefData?.data?.activeOrganizationId ?? null;
  return useInfiniteQuery({
    queryKey: ["projectList", orgId, params ?? {}],
    queryFn: ({ pageParam = 0 }) => {
      return fetchProjectsList({ page: pageParam, ...params });
    },
    getNextPageParam: (lastPage: any) => {
      const currentPage = lastPage?.data?.number ?? 0;
      const totalPages = lastPage?.data?.totalPages ?? 1;
      return currentPage + 1 < totalPages ? currentPage + 1 : undefined;
    },
    initialPageParam: 0,
    enabled: options?.enabled ?? true,
  });
};

export const useWorkspaceList = (projectId: string) =>
  useQuery({
    queryKey: ["workspaceVersionsList", projectId],
    queryFn: () => fetchWorkspacesList(projectId),
    enabled: !!projectId,
  });

export const useVersionsList = (workspaceId: string) =>
  useQuery({
    queryKey: ["versionsList", workspaceId],
    queryFn: () => fetchVersionsList(workspaceId),
    enabled: !!workspaceId,
  });

/** All projects in the current user's org — used by the invite/bulk-invite form. */
export const useOrgProjects = (enabled = true, options: { excludeSample?: boolean } = {}) => {
  const { data: prefData } = useFetchUserPreference();
  const orgId: number | null = prefData?.data?.activeOrganizationId ?? null;
  const excludeSample = options.excludeSample === true;
  return useQuery({
    queryKey: ["orgProjects", orgId, excludeSample],
    queryFn: () => fetchOrgProjectsApi({ excludeSample }),
    enabled,
    staleTime: 30_000,
  });
};
